/*
  Buscador por beneficio / vitamina (bbx) — A Granel Madrid
  ---------------------------------------------------------
  Motor de búsqueda tolerante: entiende frases largas, faltas de ortografía,
  sinónimos coloquiales y síntomas ("no puedo dormir", "algo pal colesterol",
  "magensio"), y nunca devuelve cero resultados.

  Cómo funciona
  1. Descarga una vez el índice completo del catálogo (/collections/all?view=bb-json)
     y lo cachea en sessionStorage. A partir de ahí toda la búsqueda es local.
  2. Normaliza la consulta, reconoce frases del diccionario (bbx-diccionario.js),
     luego palabras sueltas, y lo que no reconoce lo corrige por distancia de edición.
  3. Puntúa cada producto por tags + título + tipo y ordena por cobertura de términos.
  4. Si nada casa, cae a similitud de trigramas sobre los títulos.

  El diccionario vive en window.BBX_DICC y se lee de forma perezosa, así que el
  orden de carga de los <script> es indiferente.
*/
(function () {
  'use strict';
  // W permite cargar este archivo en Node (banco de pruebas) además del navegador.
  var W = typeof window !== 'undefined' ? window : (globalThis.window = {});
  if (W.__bbxInit) return;
  W.__bbxInit = true;

  var IDX_KEY = 'bbx-idx-v2';
  var IDX_TTL = 30 * 60 * 1000; // 30 min
  var PAGE_URL = '/pages/buscador';

  /* ════════════════════════════════════════════════════════════
     1. Utilidades de texto
     ════════════════════════════════════════════════════════════ */

  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Distancia Damerau-Levenshtein acotada: sale antes si supera max.
  function dist(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    if (a === b) return 0;
    var prev2 = null, prev = new Array(lb + 1), cur = new Array(lb + 1), i, j, tmp;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur[0] = i;
      var best = i;
      for (j = 1; j <= lb; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        var v = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charCodeAt(i - 1) === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < best) best = v;
      }
      if (best > max) return max + 1;
      tmp = prev2; prev2 = prev; prev = cur; cur = tmp || new Array(lb + 1);
    }
    return prev[lb];
  }

  /*
    Umbral de corrección según longitud. En palabras cortas una distancia de 2
    ya no es una errata, es otra palabra ("bonito" → "bocio"), así que solo se
    permite 1. El margen se abre a partir de 8 caracteres, donde las erratas
    reales sí se acumulan.
  */
  function maxDist(len) {
    if (len <= 4) return 0;
    if (len <= 7) return 1;
    if (len <= 10) return 2;
    return 3;
  }

  function trigrams(s) {
    var p = '  ' + s + ' ', out = [];
    for (var i = 0; i < p.length - 2; i++) out.push(p.substr(i, 3));
    return out;
  }

  // Coeficiente de Dice sobre trigramas (0..1).
  function dice(a, b) {
    if (!a || !b) return 0;
    var ta = trigrams(a), tb = trigrams(b);
    if (!ta.length || !tb.length) return 0;
    var seen = Object.create(null), hits = 0, i;
    for (i = 0; i < ta.length; i++) seen[ta[i]] = (seen[ta[i]] || 0) + 1;
    for (i = 0; i < tb.length; i++) {
      if (seen[tb[i]] > 0) { seen[tb[i]]--; hits++; }
    }
    return (2 * hits) / (ta.length + tb.length);
  }

  /* ════════════════════════════════════════════════════════════
     2. Vocabulario y etiquetas visibles
     ════════════════════════════════════════════════════════════ */

  var LABELS = {
    'bf-digestion': 'Digestión', 'bf-transito': 'Tránsito', 'bf-gases': 'Hinchazón',
    'bf-acidez': 'Acidez', 'bf-microbiota': 'Microbiota', 'bf-nauseas': 'Náuseas',
    'bf-energia': 'Energía', 'bf-deporte': 'Deporte', 'bf-musculo': 'Músculo',
    'bf-concentracion': 'Concentración', 'bf-sueno': 'Descanso', 'bf-relax': 'Relax',
    'bf-animo': 'Ánimo', 'bf-defensas': 'Defensas', 'bf-respiratorio': 'Respiratorio',
    'bf-colesterol': 'Colesterol', 'bf-corazon': 'Corazón', 'bf-tension': 'Tensión',
    'bf-circulacion': 'Circulación', 'bf-glucosa': 'Glucosa', 'bf-metabolismo': 'Metabolismo',
    'bf-saciedad': 'Saciedad', 'bf-huesos': 'Huesos', 'bf-articulaciones': 'Articulaciones',
    'bf-piel': 'Piel', 'bf-pelo-unas': 'Pelo y uñas', 'bf-antiedad': 'Antiedad',
    'bf-antioxidante': 'Antioxidante', 'bf-antiinflamatorio': 'Antiinflamatorio',
    'bf-depurativo': 'Depurativo', 'bf-diuretico': 'Diurético',
    'bf-antibacteriano': 'Antibacteriano', 'bf-vias-urinarias': 'Vías urinarias',
    'bf-menopausia': 'Menopausia', 'bf-menstruacion': 'Ciclo femenino',
    'bf-embarazo': 'Embarazo', 'bf-prostata': 'Próstata', 'bf-libido': 'Vitalidad',
    'bf-infantil': 'Para niños', 'bf-senior': 'Séniors', 'bf-vista': 'Vista',
    'bf-tiroides': 'Tiroides', 'bf-hidratacion': 'Hidratación',
    'bf-sin-cafeina': 'Sin cafeína', 'bf-estimulante': 'Estimulante',
    'antioxidantes': 'Antioxidante', 'fibra': 'Fibra', 'hierro': 'Hierro',
    'magnesio': 'Magnesio', 'calcio': 'Calcio', 'potasio': 'Potasio',
    'fosforo': 'Fósforo', 'zinc': 'Zinc', 'selenio': 'Selenio', 'yodo': 'Yodo',
    'manganeso': 'Manganeso', 'cobre': 'Cobre', 'cromo': 'Cromo',
    'omega-3': 'Omega 3', 'omega-6': 'Omega 6', 'alto-en-proteina': 'Proteína',
    'triptofano': 'Triptófano', 'melatonina': 'Melatonina',
    'sin-azucar-anadido': 'Sin azúcar', 'sin-gluten': 'Sin gluten',
    'vegano': 'Vegano', 'ecologico': 'Ecológico', 'superalimento': 'Superalimento',
    'digestion': 'Digestión', 'energia': 'Energía', 'saciante': 'Saciedad',
    'relajante': 'Relax'
  };

  // Tags que el motor considera "buscables" aunque no estén en el diccionario.
  var CANON = Object.keys(LABELS);
  var CANON_SET = Object.create(null);
  CANON.forEach(function (t) { CANON_SET[t] = true; });

  function label(tag) {
    if (LABELS[tag]) return LABELS[tag];
    if (/^vitamina-/.test(tag)) return 'Vit. ' + tag.slice(9).toUpperCase();
    return tag.charAt(0).toUpperCase() + tag.slice(1).replace(/-/g, ' ');
  }

  // Etiqueta visible → tag. Garantiza que los chips de la UI siempre resuelvan,
  // aunque el diccionario generado no cubra esa expresión.
  var REV = Object.create(null);
  Object.keys(LABELS).forEach(function (t) {
    var k = norm(LABELS[t]);
    if (!REV[k]) REV[k] = t;
  });
  ['vitamina-a', 'vitamina-b1', 'vitamina-b2', 'vitamina-b3', 'vitamina-b5', 'vitamina-b6',
    'vitamina-b9', 'vitamina-b12', 'vitamina-c', 'vitamina-d', 'vitamina-e', 'vitamina-k'
  ].forEach(function (t) {
    if (!CANON_SET[t]) { CANON_SET[t] = true; CANON.push(t); }
    REV[t.replace('-', ' ')] = t;
  });

  var STOPWORDS = ('a al algo alguna algunas alguno algunos ante antes aqui asi buenas bueno buenos ' +
    'cada como con contra cosa cosas cual cuales cuando dame dar de del desde donde dos e el ella ' +
    'ellas ello ellos en encima entre era eran es esa esas ese eso esos esta estan estar estas este ' +
    'esto estos estoy fue fuera gustaria ha hace hacer hacia han hasta hay he la las le les lo los ' +
    'mas me mejor menos mi mia mis mucha muchas mucho muchos muy nada ni no nos nosotros o os otra ' +
    'otras otro otros para pero poco por porque pues que quiero quisiera quiza recomienda recomiendas ' +
    'se ser si siempre sido sobre solo son su sus tambien tan tanto tener tengo ti tiene tienen toda ' +
    'todas todo todos tu tus un una unas uno unos usted va vais van vaya ver y ya yo hola buenas ' +
    'necesito busco buscando ando quisiera ayuda ayudar ayude sirva sirve servir vale bien ' +
    'pal pa comprar compro compra tomar tomo tomarme uso usar poner hacer haciendo dias noches ' +
    'ultimamente siempre veces vez rato poquito bastante super demasiado gustaria encantaria ' +
    'porfa favor gracias oye mira pues bueno vamos venga ' +
    // formas verbales frecuentes: sin esto el corrector las confunde con
    // términos del diccionario ("tenga" → "tensa" → relax)
    'tenga tengan tengas tuviera tuviese hubiera hubiese seas sean fuese siendo estando ' +
    'vaya vayan pueda puedan puede pueden podria podrian querria quiere quieren').split(' ');
  var STOP = Object.create(null);
  STOPWORDS.forEach(function (w) { STOP[w] = true; });

  // Palabras cortas que SÍ son producto y no deben caer como palabra vacía.
  var PROTEGIDAS = Object.create(null);
  ('te mate sal ajo sol soja uva kiwi higo nuez anis coco lino chia mijo malta cola dulce ' +
    'amargo negro verde blanco rojo azul').split(' ').forEach(function (w) { PROTEGIDAS[w] = true; });

  /* ════════════════════════════════════════════════════════════
     3. Índice del catálogo
     ════════════════════════════════════════════════════════════ */

  var index = null;          // [{t,u,p,y,g,i, _t:tituloNorm, _w:[palabras], _y:tipoNorm}]
  var lexicon = null;        // palabras que aparecen en los títulos
  var indexPromise = null;
  var indexFailed = false;

  function decorate(items) {
    lexicon = Object.create(null);
    items.forEach(function (p) {
      p._t = norm(p.t);
      p._y = norm(p.y);
      p._w = p._t.split(' ').filter(function (w) { return w.length > 2; });
      p._w.forEach(function (w) { lexicon[w] = true; });
      p._g = p.g || [];
      // Declaraciones nutricionales calculadas en el índice: permiten buscar por "fibra",
      // "proteína", "hierro", "sin azúcar"… sin depender de etiquetas escritas a mano.
      p._b = norm((p.b || []).join(" "));
    });
    return items;
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(IDX_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.ts || (Date.now() - obj.ts) > IDX_TTL) return null;
      if (!Array.isArray(obj.items) || !obj.items.length) return null;
      return obj.items;
    } catch (e) { return null; }
  }

  function writeCache(items) {
    try { sessionStorage.setItem(IDX_KEY, JSON.stringify({ ts: Date.now(), items: items })); }
    catch (e) { /* cuota llena: seguimos sin caché */ }
  }

  function fetchPage(page) {
    return fetch('/collections/all?view=bb-json&sort_by=best-selling&page=' + page, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    var cached = readCache();
    if (cached) {
      index = decorate(cached);
      indexPromise = Promise.resolve(index);
      return indexPromise;
    }
    indexPromise = fetchPage(1).then(function (first) {
      var pages = Math.max(1, Math.min(first.pages || 1, 40));
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(fetchPage(p));
      return Promise.all(rest).then(function (more) {
        var all = first.products || [];
        more.forEach(function (d) { all = all.concat(d.products || []); });
        writeCache(all);
        index = decorate(all);
        return index;
      });
    }).catch(function (e) {
      indexFailed = true;
      throw e;
    });
    return indexPromise;
  }

  /* ════════════════════════════════════════════════════════════
     4. Interpretación de la consulta
     ════════════════════════════════════════════════════════════ */

  function dicc() {
    return W.BBX_DICC || { frases: {}, palabras: {} };
  }

  // Corrige la palabra "vitamina" y compañía ANTES de aplicar los patrones
  // químicos: si no, "vitamna c" nunca llegaría a ser "vitamina-c".
  var FORMAS = [
    { canon: 'vitamina', min: 5, max: 11, tol: 2 },
    { canon: 'omega', min: 4, max: 7, tol: 1 },
    { canon: 'proteina', min: 6, max: 11, tol: 2 },
    { canon: 'mineral', min: 5, max: 9, tol: 1 },
    { canon: 'antioxidante', min: 9, max: 15, tol: 3 }
  ];

  function fixFormas(s, fixes) {
    return s.split(' ').map(function (w) {
      if (!w || w.indexOf('-') > -1) return w;
      for (var i = 0; i < FORMAS.length; i++) {
        var f = FORMAS[i];
        if (w === f.canon || w.length < f.min || w.length > f.max) continue;
        if (w.charAt(0) !== f.canon.charAt(0)) continue;
        if (dist(w, f.canon, f.tol) <= f.tol) {
          if (fixes && w !== f.canon + 's') fixes.push({ from: w, to: f.canon });
          return f.canon;
        }
      }
      return w;
    }).join(' ');
  }

  // Normaliza formas de vitaminas, omegas y nombres químicos a slugs canónicos.
  function canonizeChem(s) {
    return s
      // El grupo de la cifra tiene que englobar su propio espacio: si no,
      // "vitamina c y zinc" se comería la conjunción y daría "vitamina-cy".
      .replace(/\bvit(?:amina|aminas|s)?\s*\.?\s*-?\s*([abcdek])(?:\s*-\s*|\s*)?(\d{1,2})?\b/g,
        function (m, letra, num) { return 'vitamina-' + letra + (num || ''); })
      .replace(/\bpro\s*vitamina[- ]?a\b/g, 'vitamina-a')
      .replace(/\bomega\s*-?\s*([3569])\b/g, 'omega-$1')
      .replace(/\bacido\s+folico\b/g, 'vitamina-b9')
      .replace(/\bfolatos?\b/g, 'vitamina-b9')
      .replace(/\bacido\s+ascorbico\b/g, 'vitamina-c')
      .replace(/\bcobalamina\b/g, 'vitamina-b12')
      .replace(/\btocoferol(es)?\b/g, 'vitamina-e')
      .replace(/\bretinol\b/g, 'vitamina-a')
      .replace(/\btiamina\b/g, 'vitamina-b1')
      .replace(/\briboflavina\b/g, 'vitamina-b2')
      .replace(/\bniacina\b/g, 'vitamina-b3')
      .replace(/\bpiridoxina\b/g, 'vitamina-b6')
      .replace(/\bcolecalciferol\b/g, 'vitamina-d')
      .replace(/\bfiloquinona\b/g, 'vitamina-k')
      .replace(/\bproteinas?\b/g, 'alto-en-proteina')
      .replace(/\bsin\s+azucar(\s+anadid[oa])?\b/g, 'sin-azucar-anadido')
      .replace(/\bsin\s+gluten\b/g, 'sin-gluten')
      .replace(/\bsin\s+lactosa\b/g, 'vegano');
  }

  // Si "vitamina-d3" no existe, prueba "vitamina-d".
  function fixVitamin(tag) {
    if (CANON_SET[tag]) return tag;
    var m = /^vitamina-([a-k])(\d+)$/.exec(tag);
    if (m) {
      if (CANON_SET['vitamina-' + m[1] + m[2]]) return 'vitamina-' + m[1] + m[2];
      if (CANON_SET['vitamina-' + m[1]]) return 'vitamina-' + m[1];
    }
    if (/^vitamina-[a-k]\d{0,2}$/.test(tag)) return tag; // puede existir como tag aunque no lo listemos
    return null;
  }

  // Vocabulario contra el que se corrigen las faltas de ortografía.
  var fuzzyPool = null;
  function buildFuzzyPool() {
    var d = dicc(), pool = Object.create(null), k;
    for (k in d.palabras) pool[k] = 'dicc';
    CANON.forEach(function (t) { pool[t.replace(/^bf-/, '')] = 'tag:' + t; });
    if (lexicon) for (k in lexicon) if (!pool[k]) pool[k] = 'texto';
    fuzzyPool = Object.keys(pool).map(function (w) { return { w: w, kind: pool[w], len: w.length }; });
    return fuzzyPool;
  }

  function fuzzyLookup(token) {
    if (!fuzzyPool) buildFuzzyPool();
    var max = maxDist(token.length);
    if (max === 0) return null;
    var best = null, bestD = max + 1;
    for (var i = 0; i < fuzzyPool.length; i++) {
      var c = fuzzyPool[i];
      if (Math.abs(c.len - token.length) > max) continue;
      var d = dist(token, c.w, max);
      if (d < bestD || (d === bestD && best && c.w.length < best.w.length)) {
        bestD = d; best = c;
      }
    }
    return bestD <= max ? best : null;
  }

  /*
    Devuelve { concepts:[{kind:'tag'|'text', v, w}], fixes:[{from,to}], raw }
  */
  function parseQuery(q) {
    var d = dicc();
    var concepts = [], fixes = [];
    var text = canonizeChem(fixFormas(norm(q), fixes));
    var tokens = text.split(' ').filter(Boolean);
    var used = new Array(tokens.length);
    var seen = Object.create(null);

    function push(kind, v, w) {
      if (!v) return;
      var key = kind + ':' + v;
      if (seen[key]) { if (w > seen[key].w) seen[key].w = w; return; }
      var c = { kind: kind, v: v, w: w };
      seen[key] = c;
      concepts.push(c);
    }
    function pushTags(list, w) {
      (list || []).forEach(function (t) {
        var fixed = CANON_SET[t] ? t : fixVitamin(t) || t;
        push('tag', fixed, w);
      });
    }

    // 4.1 — frases del diccionario, de más larga a más corta
    var maxN = Math.min(6, tokens.length);
    for (var n = maxN; n >= 2; n--) {
      for (var i = 0; i + n <= tokens.length; i++) {
        var libre = true;
        for (var k = i; k < i + n; k++) if (used[k]) { libre = false; break; }
        if (!libre) continue;
        var frase = tokens.slice(i, i + n).join(' ');
        var hitFrase = d.frases[frase] || (REV[frase] ? [REV[frase]] : null);
        if (hitFrase) {
          pushTags(hitFrase, 1);
          for (var m = i; m < i + n; m++) used[m] = true;
        }
      }
    }

    // 4.2 — tokens sueltos
    for (var t = 0; t < tokens.length; t++) {
      if (used[t]) continue;
      var tok = tokens[t];
      if (tok.length < 2) continue;

      if (CANON_SET[tok]) { pushTags([tok], 1); continue; }
      var vit = /^vitamina-/.test(tok) ? fixVitamin(tok) : null;
      if (vit) { pushTags([vit], 1); continue; }
      if (d.palabras[tok]) { pushTags(d.palabras[tok], 1); continue; }
      if (REV[tok]) { pushTags([REV[tok]], 1); continue; }
      if (CANON_SET['bf-' + tok]) { pushTags(['bf-' + tok], 1); continue; }

      // singular/plural rápido antes de recurrir a la distancia de edición
      var sing = tok.replace(/(es|s)$/, '');
      if (sing !== tok && sing.length > 2) {
        if (d.palabras[sing]) { pushTags(d.palabras[sing], 1); continue; }
        if (CANON_SET[sing]) { pushTags([sing], 1); continue; }
        if (CANON_SET['bf-' + sing]) { pushTags(['bf-' + sing], 1); continue; }
      }

      // Las palabras vacías se descartan antes de mirar el catálogo: si no,
      // "para" o "natural" (que aparecen en títulos) ensucian los resultados.
      // Las protegidas son producto de verdad ("té", "sal", "ajo") y se salvan.
      if (STOP[tok] && !PROTEGIDAS[tok]) continue;

      // palabra que aparece en algún título del catálogo → búsqueda por texto
      if (lexicon && lexicon[tok]) { push('text', tok, 1); continue; }
      if (lexicon && sing !== tok && lexicon[sing]) { push('text', sing, 1); continue; }

      // corrección ortográfica
      var hit = fuzzyLookup(tok);
      if (hit) {
        fixes.push({ from: tok, to: hit.w });
        if (hit.kind === 'dicc') pushTags(d.palabras[hit.w], 0.85);
        else if (hit.kind.indexOf('tag:') === 0) pushTags([hit.kind.slice(4)], 0.85);
        else push('text', hit.w, 0.85);
        continue;
      }

      // no sabemos qué es: lo dejamos como texto libre, ya lo pescará el trigrama
      if (tok.length >= 3) push('text', tok, 0.6);
    }

    return { concepts: concepts, fixes: fixes, raw: q.trim(), norm: text };
  }

  /* ════════════════════════════════════════════════════════════
     5. Puntuación y búsqueda
     ════════════════════════════════════════════════════════════ */

  function scoreProduct(p, concepts) {
    var s = 0, matched = [];
    for (var i = 0; i < concepts.length; i++) {
      var c = concepts[i], got = 0;
      if (c.kind === 'tag') {
        if (p._g.indexOf(c.v) > -1) got = 12;
        else if (c.v === 'bf-antioxidante' && p._g.indexOf('antioxidantes') > -1) got = 12;
        else if (c.v === 'bf-digestion' && p._g.indexOf('digestion') > -1) got = 11;
        else if (c.v === 'bf-energia' && p._g.indexOf('energia') > -1) got = 11;
        else if (c.v === 'bf-saciedad' && p._g.indexOf('saciante') > -1) got = 11;
        else if (c.v === 'bf-relax' && p._g.indexOf('relajante') > -1) got = 11;
        else if (p._t.indexOf(c.v.replace(/^bf-/, '')) > -1) got = 5;
      } else {
        var w = c.v;
        if (p._w.indexOf(w) > -1) got = 10;
        else if (p._t.indexOf(w) > -1) got = 8;
        else if (p._y.indexOf(w) > -1) got = 5;
        else if (p._b && p._b.indexOf(w) > -1) got = 6;
        else {
          for (var j = 0; j < p._w.length; j++) {
            var pw = p._w[j];
            if (w.length >= 4 && pw.indexOf(w) === 0) { got = 7; break; }
            if (w.length >= 5 && Math.abs(pw.length - w.length) <= 2 && dist(w, pw, 1) <= 1) { got = 6; break; }
          }
          if (!got) {
            for (var g = 0; g < p._g.length; g++) {
              if (p._g[g].indexOf(w) > -1) { got = 4; break; }
            }
          }
        }
      }
      if (got) { s += got * c.w; matched.push(c.v); }
    }
    if (!s) return null;
    var coverage = matched.length / concepts.length;
    s = s * (0.5 + 0.5 * coverage) + matched.length * 5;
    return { p: p, s: s, matched: matched, full: matched.length === concepts.length };
  }

  function runSearch(concepts) {
    if (!index) return [];
    var out = [];
    for (var i = 0; i < index.length; i++) {
      var r = scoreProduct(index[i], concepts);
      if (r) { r.idx = i; out.push(r); }
    }
    out.sort(function (a, b) { return (b.s - a.s) || (a.idx - b.idx); });
    return out;
  }

  // Último recurso: parecido de trigramas contra los títulos, bajando el listón
  // en dos pasadas. Si aun así no hay nada, devuelve los más vendidos, para que
  // el usuario nunca se quede mirando una pantalla vacía.
  function fallbackSimilar(qnorm) {
    if (!index) return [];
    var qw = qnorm.split(' ').filter(function (w) { return w.length >= 4; });
    var puntuado = [];
    for (var i = 0; i < index.length; i++) {
      var sim = dice(qnorm, index[i]._t);
      var wordsSim = 0;
      for (var j = 0; j < qw.length; j++) {
        for (var k = 0; k < index[i]._w.length; k++) {
          var s2 = dice(qw[j], index[i]._w[k]);
          if (s2 > wordsSim) wordsSim = s2;
        }
      }
      puntuado.push({ p: index[i], s: Math.max(sim, wordsSim * 0.9), matched: [], full: false, idx: i });
    }
    puntuado.sort(function (a, b) { return (b.s - a.s) || (a.idx - b.idx); });

    var umbrales = [0.28, 0.15];
    for (var u = 0; u < umbrales.length; u++) {
      var hit = puntuado.filter(function (r) { return r.s > umbrales[u]; });
      if (hit.length) return hit.slice(0, 24);
    }
    return index.slice(0, 12).map(function (p, n) {
      return { p: p, s: 0, matched: [], full: false, idx: n, populares: true };
    });
  }

  /* ════════════════════════════════════════════════════════════
     6. Fallback sin índice (servidor, por tag)
     ════════════════════════════════════════════════════════════ */

  function serverTagSearch(tags) {
    var fetches = tags.slice(0, 4).map(function (t) {
      return fetch('/collections/all/' + encodeURIComponent(t) + '?view=bb-json&sort_by=best-selling&page=1')
        .then(function (r) { return r.json(); })
        .catch(function () { return { products: [] }; });
    });
    return Promise.all(fetches).then(function (res) {
      var map = Object.create(null), order = 0;
      res.forEach(function (d, i) {
        (d.products || []).forEach(function (p) {
          if (!map[p.u]) map[p.u] = { p: p, matched: [], idx: order++, s: 1 };
          if (map[p.u].matched.indexOf(tags[i]) === -1) map[p.u].matched.push(tags[i]);
        });
      });
      var items = Object.keys(map).map(function (k) { return map[k]; });
      items.forEach(function (it) { it.full = it.matched.length === tags.length; });
      items.sort(function (a, b) { return (b.matched.length - a.matched.length) || (a.idx - b.idx); });
      return items;
    });
  }

  /* ════════════════════════════════════════════════════════════
     7. Interfaz
     ════════════════════════════════════════════════════════════ */

  var SIN_RESULTADOS = 'No hemos encontrado nada exacto. Estos son los productos más parecidos:';

  function money(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function init(root) {
    if (root.hasAttribute('data-bbx-ready')) return;
    root.setAttribute('data-bbx-ready', '1');

    var mode = root.dataset.mode || 'inline';
    var pageUrl = root.dataset.pageUrl || PAGE_URL;
    var input = root.querySelector('[data-bbx-input]');
    var clearBtn = root.querySelector('[data-bbx-clear]');
    var status = root.querySelector('[data-bbx-status]');
    var note = root.querySelector('[data-bbx-note]');
    var grid = root.querySelector('[data-bbx-grid]');
    var moreBtn = root.querySelector('[data-bbx-more]');
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-bbx-chip]'));
    var toggles = Array.prototype.slice.call(root.querySelectorAll('[data-bbx-chiptoggle]'));
    var LIMIT = mode === 'full' ? 0 : 12;
    var timer = null, token = 0, pending = null;

    // Se construye con la API del DOM (sin innerHTML) para que ningún dato del
    // catálogo pueda inyectar marcado.
    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    function card(p, matched, total) {
      var a = el('a', 'bbx-card');
      a.href = p.u;

      if (p.i) {
        var img = el('img');
        img.src = p.i;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 300;
        img.height = 300;
        a.appendChild(img);
      } else {
        a.appendChild(el('div', 'bbx-card-noimg'));
      }

      var body = el('div', 'bbx-card-body');
      body.appendChild(el('p', 'bbx-card-title', p.t));

      var price = el('span', 'bbx-card-price', money(p.p) + ' ');
      price.appendChild(el('small', null, '/ 100g'));
      body.appendChild(price);

      if (matched && matched.length && total > 1) {
        var wrap = el('div', 'bbx-badges');
        matched.slice(0, 4).forEach(function (t) {
          var b = el('span', 'bbx-badge', label(t));
          if (matched.length === total) b.classList.add('is-full');
          wrap.appendChild(b);
        });
        body.appendChild(wrap);
      }

      a.appendChild(body);
      return a;
    }

    function skeletons(n) {
      grid.innerHTML = '';
      for (var i = 0; i < n; i++) {
        var d = document.createElement('div');
        d.className = 'bbx-skel';
        grid.appendChild(d);
      }
    }

    function reset() {
      grid.innerHTML = '';
      status.textContent = '';
      note.innerHTML = '';
      moreBtn.classList.remove('is-visible');
    }

    function renderNote(parsed, aprox) {
      note.innerHTML = '';
      if (parsed && parsed.fixes.length) {
        var frag = document.createElement('span');
        frag.appendChild(document.createTextNode('Hemos entendido '));
        parsed.fixes.forEach(function (f, i) {
          if (i) frag.appendChild(document.createTextNode(', '));
          var b = document.createElement('b');
          b.textContent = f.to;
          var s = document.createElement('s');
          s.textContent = f.from;
          frag.appendChild(b);
          frag.appendChild(document.createTextNode(' '));
          frag.appendChild(s);
        });
        note.appendChild(frag);
      }
      var tags = parsed ? parsed.concepts.filter(function (c) { return c.kind === 'tag'; }) : [];
      if (tags.length) {
        var sp = document.createElement('span');
        sp.style.display = 'block';
        sp.textContent = 'Buscando: ' + tags.map(function (c) { return label(c.v); }).join(' · ');
        note.appendChild(sp);
      }
      if (aprox) {
        var w = document.createElement('span');
        w.style.display = 'block';
        w.textContent = aprox === 'populares'
          ? 'No tenemos nada etiquetado con eso todavía. Mientras tanto, lo más vendido:'
          : SIN_RESULTADOS;
        note.appendChild(w);
      }
    }

    function render(items, parsed, aprox) {
      grid.innerHTML = '';
      var total = items.length;
      var shown = LIMIT ? items.slice(0, LIMIT) : items;
      var nConcepts = parsed ? parsed.concepts.length : 1;
      // Un fragmento evita recalcular el layout una vez por tarjeta: en la
      // página completa se pintan varios cientos de golpe.
      var frag = document.createDocumentFragment();
      shown.forEach(function (it) {
        frag.appendChild(card(it.p, it.matched, nConcepts));
      });
      grid.appendChild(frag);
      renderNote(parsed, aprox);
      if (!total) {
        status.textContent = 'Sin resultados. Prueba con «magnesio», «para dormir» o «digestión».';
        moreBtn.classList.remove('is-visible');
        return;
      }
      var completos = items.filter(function (it) { return it.full; }).length;
      status.textContent = total + (total === 1 ? ' producto' : ' productos') +
        (nConcepts > 1 && completos ? ' · ' + completos + ' con todo lo que buscas' : '') +
        (total > shown.length ? ' · mostrando ' + shown.length : '');
      if (LIMIT && total > LIMIT) {
        moreBtn.textContent = 'Ver los ' + total + ' productos →';
        moreBtn.href = pageUrl + '?q=' + encodeURIComponent(parsed ? parsed.raw : '');
        moreBtn.classList.add('is-visible');
      } else {
        moreBtn.classList.remove('is-visible');
      }
    }

    function doSearch(q) {
      var my = ++token;
      var parsed = parseQuery(q);
      // fix 2026-09-02 — modo "search" (página /search): solo se muestra si la consulta
      // se entiende como necesidad o nutriente; si no, el bloque queda oculto.
      if (mode === 'search') {
        var hayConcepto = parsed.concepts.length > 0;
        root.hidden = !hayConcepto;
        if (!hayConcepto) return;
      }
      var items = parsed.concepts.length ? runSearch(parsed.concepts) : [];
      var aprox = false;
      if (!items.length) {
        items = fallbackSimilar(parsed.norm);
        aprox = (items[0] && items[0].populares) ? 'populares' : true;
      } else if (items[0].s < 6) {
        aprox = true;
      }
      if (my !== token) return;
      render(items, parsed, aprox);
    }

    function search(q) {
      q = (q || '').trim();
      clearBtn.classList.toggle('is-visible', q.length > 0);
      if (q.length < 2) { reset(); pending = null; return; }
      if (mode === 'full') {
        try { history.replaceState(null, '', location.pathname + '?q=' + encodeURIComponent(q)); } catch (e) { }
      }

      if (index) { doSearch(q); return; }

      if (indexFailed) {
        // sin índice: al menos resolvemos los tags contra el servidor
        var parsed = parseQuery(q);
        var tags = parsed.concepts.filter(function (c) { return c.kind === 'tag'; }).map(function (c) { return c.v; });
        if (!tags.length) {
          status.textContent = 'El buscador no está disponible ahora mismo. Prueba en /search.';
          return;
        }
        status.textContent = 'Buscando…';
        serverTagSearch(tags).then(function (items) { render(items, parsed, false); });
        return;
      }

      pending = q;
      status.textContent = 'Preparando el buscador…';
      skeletons(LIMIT || 12);
      loadIndex().then(function () {
        buildFuzzyPool();
        if (pending) { var p = pending; pending = null; doSearch(p); }
      }).catch(function () {
        search(q); // reintenta ya por la rama indexFailed
      });
    }

    input.addEventListener('input', function () {
      chips.forEach(function (c) { c.classList.remove('is-active'); });
      clearTimeout(timer);
      timer = setTimeout(function () { search(input.value); }, 220);
    });

    input.addEventListener('focus', function () { loadIndex().catch(function () { }); }, { once: true });

    clearBtn.addEventListener('click', function () {
      input.value = '';
      chips.forEach(function (c) { c.classList.remove('is-active'); });
      search('');
      input.focus();
    });

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        chip.classList.toggle('is-active');
        var activos = chips.filter(function (c) { return c.classList.contains('is-active'); })
          .map(function (c) { return c.dataset.q || c.textContent.trim(); });
        input.value = activos.join(' y ');
        search(input.value);
      });
    });

    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Se busca dentro del propio componente, no por id: el buscador puede
        // renderizarse dos veces en la misma página (home + sección).
        var group = btn.previousElementSibling;
        if (!group || !group.classList.contains('bbx-chips')) {
          group = root.querySelector('.bbx-chips[data-bbx-collapsible]');
        }
        if (!group) return;
        var collapsed = group.classList.toggle('is-collapsed');
        btn.textContent = collapsed ? btn.dataset.more : btn.dataset.less;
      });
    });

    if (mode === 'full' || mode === 'search') {
      var q0 = root.dataset.q || new URLSearchParams(location.search).get('q');
      if (q0) { input.value = q0; search(q0); }
    }
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bbx]'), init);
  }

  function warmup() {
    if (!document.querySelector('[data-bbx]')) return;
    var go = function () { loadIndex().catch(function () { }); };
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 3000 });
    else setTimeout(go, 1200);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { initAll(); warmup(); });
    } else {
      initAll(); warmup();
    }
    document.addEventListener('shopify:section:load', initAll);
  }

  // Banco de pruebas en Node: node banco-pruebas.js
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      norm: norm,
      dist: dist,
      dice: dice,
      label: label,
      parseQuery: parseQuery,
      runSearch: runSearch,
      fallbackSimilar: fallbackSimilar,
      cargarIndiceDePrueba: function (items) {
        index = decorate(items);
        fuzzyPool = null;
        buildFuzzyPool();
        return index.length;
      }
    };
  }
})();
